package no.vareflow.app.sync

import no.vareflow.app.domain.*

/**
 * Converts between app models and the field maps stored in Firestore, and decides how incoming
 * cloud data is merged with local data. Field names are a contract with `firestore.rules` and with
 * other devices: do not rename them.
 *
 * Pure functions: the current time and cloud context are passed in, so every rule is testable.
 */
object CloudMapping {

    /** Firestore document id derived from a free-text name (category names, department keys). */
    fun safeDocumentId(value: String): String = value
        .trim()
        .lowercase()
        .replace(Regex("[^a-z0-9æøå_-]+"), "-")
        .trim('-')
        .ifBlank { randomId() }

    /** Count-entry document id: legacy "bar" entries use the product id alone. */
    fun countEntryDocumentId(departmentId: String, productId: String): String =
        if (departmentId == "bar") productId else safeDocumentId("${departmentId}__$productId")

    // ---------- Products ----------

    fun productToCloud(product: Product, companyId: String, nowLocal: Long): Map<String, Any?> = mapOf(
        "id" to product.id,
        "companyId" to companyId,
        "varenummer" to product.varenummer,
        "name" to product.name,
        "unit" to product.unit,
        "pricePerUnit" to product.pricePerUnit,
        "category" to product.category,
        "categoryAssignmentId" to safeDocumentId(product.category),
        "vat" to product.vat,
        "supplier" to product.supplier,
        "importStatus" to product.importStatus,
        "dataQuality" to product.dataQuality,
        "sourceRow" to product.sourceRow,
        "sourceCategory" to product.sourceCategory,
        "aliases" to product.aliases,
        "active" to product.active,
        "undergruppe" to product.undergruppe,
        "updatedAtLocal" to nowLocal
    )

    /** Null when the document has no id. Missing fields get the same defaults as before. */
    fun productFromCloud(map: Map<String, Any?>): Product? {
        val id = map["id"]?.toString().orEmpty()
        if (id.isBlank()) return null
        return Product(
            id = id,
            varenummer = map["varenummer"]?.toString().orEmpty(),
            name = map["name"]?.toString().orEmpty(),
            unit = map["unit"]?.toString().orEmpty().ifBlank { "stk" },
            pricePerUnit = (map["pricePerUnit"] as? Number)?.toDouble() ?: 0.0,
            category = map["category"]?.toString().orEmpty(),
            vat = normalizeProductVat((map["vat"] as? Number)?.toDouble() ?: 15.0),
            supplier = map["supplier"]?.toString().orEmpty(),
            importStatus = map["importStatus"]?.toString().orEmpty().ifBlank { "Klar" },
            dataQuality = map["dataQuality"]?.toString().orEmpty(),
            sourceRow = (map["sourceRow"] as? Number)?.toInt(),
            sourceCategory = map["sourceCategory"]?.toString().orEmpty(),
            aliases = (map["aliases"] as? List<*>)?.mapNotNull { it?.toString() }.orEmpty(),
            active = map["active"] as? Boolean ?: true,
            undergruppe = map["undergruppe"]?.toString().orEmpty()
        )
    }

    // ---------- Companies ----------

    /** companies/{id}: name, short name, Firmakode and status (logo lives in branding/logo). */
    fun companyToCloud(company: CompanyProfile): Map<String, Any?> = mapOf(
        "name" to company.name,
        "shortName" to company.shortName,
        "code" to company.code,
        "status" to company.status.name,
        "headerDisplay" to company.headerDisplay.name,
        // Read by app versions before 0.12.2.
        "showNameWithLogo" to (company.headerDisplay != HeaderDisplay.LOGO)
    )

    // ---------- Support (support/{id}, support/{id}/messages/{id}) ----------

    /** A new conversation; the times are filled in as server time by the caller. */
    fun supportTicketToCloud(ticket: SupportTicket): Map<String, Any?> = mapOf(
        "companyId" to ticket.companyId,
        "companyName" to ticket.companyName,
        "uid" to ticket.uid,
        "userName" to ticket.userName,
        "userEmail" to ticket.userEmail,
        "category" to ticket.category.name,
        "subject" to ticket.subject.trim().take(SupportRules.MAX_SUBJECT),
        "status" to ticket.status.name,
        "unreadByOwner" to ticket.unreadByOwner,
        "unreadByUser" to ticket.unreadByUser,
        "appVersion" to ticket.appVersion,
        "platform" to ticket.platform,
        "language" to ticket.language
    )

    fun supportTicketFromCloud(map: Map<String, Any?>): SupportTicket = SupportTicket(
        id = map["id"]?.toString().orEmpty(),
        companyId = map["companyId"]?.toString().orEmpty(),
        companyName = map["companyName"]?.toString().orEmpty(),
        uid = map["uid"]?.toString().orEmpty(),
        userName = map["userName"]?.toString().orEmpty(),
        userEmail = map["userEmail"]?.toString().orEmpty(),
        category = runCatching { SupportCategory.valueOf(map["category"]?.toString().orEmpty()) }.getOrDefault(SupportCategory.QUESTION),
        subject = map["subject"]?.toString().orEmpty(),
        status = runCatching { SupportStatus.valueOf(map["status"]?.toString().orEmpty()) }.getOrDefault(SupportStatus.OPEN),
        unreadByOwner = map["unreadByOwner"] == true,
        unreadByUser = map["unreadByUser"] == true,
        createdAt = (map["createdAt"] as? Number)?.toLong() ?: 0L,
        updatedAt = (map["updatedAt"] as? Number)?.toLong() ?: (map["createdAt"] as? Number)?.toLong() ?: 0L,
        appVersion = map["appVersion"]?.toString().orEmpty(),
        platform = map["platform"]?.toString().orEmpty(),
        language = map["language"]?.toString().orEmpty()
    )

    fun supportMessageToCloud(message: SupportMessage): Map<String, Any?> = mapOf(
        "from" to message.from,
        "authorName" to message.authorName,
        "text" to message.text.trim().take(SupportRules.MAX_TEXT)
    )

    fun supportMessageFromCloud(map: Map<String, Any?>): SupportMessage = SupportMessage(
        id = map["id"]?.toString().orEmpty(),
        from = if (map["from"] == SupportMessage.FROM_OWNER) SupportMessage.FROM_OWNER else SupportMessage.FROM_USER,
        authorName = map["authorName"]?.toString().orEmpty(),
        text = map["text"]?.toString().orEmpty(),
        createdAt = (map["createdAt"] as? Number)?.toLong() ?: 0L
    )

    /** companies/{id}/branding/logo (see [CompanyLogo]); the images as Base64 text. */
    fun logoToCloud(logo: CompanyLogo, updatedBy: String, updatedAt: Long): Map<String, Any?> = mapOf(
        "png" to Crypto.base64Encode(logo.png),
        "jpeg" to Crypto.base64Encode(logo.jpeg),
        "width" to logo.width.toLong(),
        "height" to logo.height.toLong(),
        "background" to if (logo.darkBackground) "dark" else "light",
        "updatedBy" to updatedBy,
        "updatedAt" to updatedAt
    )

    /** The logo, or null when the document is missing, empty or damaged. */
    fun logoFromCloud(map: Map<String, Any?>?): CompanyLogo? {
        if (map == null) return null
        val png = Crypto.base64Decode(map["png"]?.toString().orEmpty()) ?: return null
        val jpeg = Crypto.base64Decode(map["jpeg"]?.toString().orEmpty()) ?: return null
        val width = (map["width"] as? Number)?.toInt() ?: return null
        val height = (map["height"] as? Number)?.toInt() ?: return null
        if (png.isEmpty() || jpeg.isEmpty() || width <= 0 || height <= 0) return null
        return CompanyLogo(png, jpeg, width, height, darkBackground = map["background"]?.toString() == "dark")
    }

    fun companyFromCloud(id: String, map: Map<String, Any?>): CompanyProfile = CompanyProfile(
        id = id,
        name = map["name"]?.toString().orEmpty().ifBlank { id },
        shortName = map["shortName"]?.toString().orEmpty(),
        code = map["code"]?.toString().orEmpty(),
        status = runCatching { CompanyStatus.valueOf(map["status"]?.toString().orEmpty()) }.getOrDefault(CompanyStatus.ACTIVE),
        headerDisplay = HeaderDisplay.fromCloud(map["headerDisplay"])
    )

    /**
     * companyCodes/{code}: the public lookup from a Firmakode to its company. Anyone may read one
     * code (the login page shows the company before sign-in); listing all codes is not allowed.
     */
    fun companyCodeToCloud(company: CompanyProfile): Map<String, Any?> = mapOf(
        "companyId" to company.id,
        "name" to company.name,
        "shortName" to company.shortName
    )

    // ---------- Memberships (cloud users) ----------

    fun membershipToCloud(user: UserAccount, locationId: String, locationName: String, nowLocal: Long): Map<String, Any?> = mapOf(
        // The Firebase uid again as a field, so "which companies am I in?" can be one
        // collection-group query on memberships (rules check uid == the signed-in user).
        "uid" to user.id,
        "displayName" to user.name,
        "email" to user.username,
        "role" to user.role.name,
        "permissions" to user.permissions.map { it.name },
        "active" to user.active,
        // Time-limited helper: the last moment (ms) the person may work; null = no limit.
        // firestore.rules refuse everything in the company after it.
        "accessUntil" to user.accessUntil,
        "locationId" to locationId,
        "locationName" to locationName,
        "updatedAtLocal" to nowLocal
    )

    /**
     * Cloud membership → local account. ADMIN, or the wildcard permission "*", gets every
     * permission; unknown permission names are ignored; an empty list falls back to the role's
     * defaults. Cloud accounts never carry a local password.
     */
    fun membershipFromCloud(map: Map<String, Any?>, allowedCategories: Set<String>): UserAccount? {
        val uid = map["id"]?.toString().orEmpty()
        if (uid.isBlank()) return null
        val role = runCatching { Role.valueOf(map["role"]?.toString().orEmpty().uppercase()) }.getOrDefault(Role.EMPLOYEE)
        val rawPermissions = (map["permissions"] as? List<*>)?.mapNotNull { it?.toString() }?.toSet().orEmpty()
        val permissions = if (role == Role.ADMIN || "*" in rawPermissions) {
            defaultPermissions(Role.ADMIN)
        } else {
            rawPermissions.mapNotNull { raw -> runCatching { Permission.valueOf(raw) }.getOrNull() }.toSet()
                .ifEmpty { defaultPermissions(role) }
        }
        return UserAccount(
            id = uid,
            name = map["displayName"]?.toString().orEmpty().ifBlank { map["email"]?.toString().orEmpty() },
            username = map["email"]?.toString().orEmpty(),
            password = "",
            role = role,
            allowedCategories = allowedCategories,
            permissions = permissions,
            accessUntil = (map["accessUntil"] as? Number)?.toLong(),
            active = map["active"] as? Boolean ?: true
        )
    }

    /** In cloud mode a user's categories are exactly those whose assignment lists them. */
    fun categoriesAssignedTo(userId: String, assignments: List<CategoryAssignment>): Set<String> =
        assignments.filter { userId in it.assigneeIds }.map { it.category }.toSet()

    // ---------- Monthly inventory sessions ----------

    fun inventorySessionToCloud(
        session: InventorySession,
        departments: List<InventoryDepartment>,
        companyId: String,
        locationId: String,
        locationName: String,
        nowLocal: Long
    ): Map<String, Any?> = mapOf(
        "id" to session.id,
        "companyId" to companyId,
        "locationId" to locationId,
        "locationName" to locationName,
        "yearMonth" to session.yearMonth,
        "status" to session.status.name,
        "createdAtLocal" to session.createdAt,
        "closedAtLocal" to session.closedAt,
        "snapshotVersion" to session.snapshotVersion,
        "departments" to departments.map { department ->
            mapOf("id" to department.id, "name" to department.name, "sortOrder" to department.sortOrder, "categories" to department.categories)
        },
        "updatedAtLocal" to nowLocal
    )

    /** A session read from the cloud, with the departments stored on it (may be empty). */
    data class RemoteSession(val session: InventorySession, val departments: List<InventoryDepartment>)

    /** Null when the document has no id. Blank fields fall back to the signed-in company/location. */
    fun inventorySessionFromCloud(
        map: Map<String, Any?>,
        fallbackCompanyId: String,
        fallbackLocationId: String,
        fallbackLocationName: String,
        fallbackYearMonth: String,
        nowLocal: Long
    ): RemoteSession? {
        val id = map["id"]?.toString().orEmpty()
        if (id.isBlank()) return null
        val session = InventorySession(
            id = id,
            companyId = map["companyId"]?.toString().orEmpty().ifBlank { fallbackCompanyId },
            locationId = map["locationId"]?.toString().orEmpty().ifBlank { fallbackLocationId },
            locationName = map["locationName"]?.toString().orEmpty().ifBlank { fallbackLocationName },
            yearMonth = map["yearMonth"]?.toString().orEmpty().ifBlank { fallbackYearMonth },
            status = runCatching { InventoryStatus.valueOf(map["status"]?.toString().orEmpty()) }.getOrDefault(InventoryStatus.DRAFT),
            createdAt = (map["createdAtLocal"] as? Number)?.toLong() ?: nowLocal,
            closedAt = (map["closedAtLocal"] as? Number)?.toLong(),
            snapshotVersion = (map["snapshotVersion"] as? Number)?.toInt() ?: 0
        )
        val departments = (map["departments"] as? List<*>)
            ?.mapNotNull { raw ->
                val d = raw as? Map<*, *> ?: return@mapNotNull null
                val departmentId = d["id"]?.toString().orEmpty()
                val departmentName = d["name"]?.toString().orEmpty()
                if (departmentId.isBlank() || departmentName.isBlank()) null else InventoryDepartment(
                    id = departmentId,
                    sessionId = id,
                    name = departmentName,
                    sortOrder = (d["sortOrder"] as? Number)?.toInt() ?: 0,
                    categories = (d["categories"] as? List<*>)?.mapNotNull { it?.toString() }.orEmpty()
                )
            }.orEmpty()
        return RemoteSession(session, departments)
    }

    /**
     * Should an incoming cloud session replace the local copy? Not when it would reopen a month
     * that is already CLOSED/ARCHIVED here with an equal or older snapshot (a stale echo).
     */
    fun acceptIncomingSession(local: InventorySession, incoming: InventorySession): Boolean {
        val incomingWouldReopenOlder =
            local.status in setOf(InventoryStatus.CLOSED, InventoryStatus.ARCHIVED) &&
                incoming.status in setOf(InventoryStatus.DRAFT, InventoryStatus.ACTIVE) &&
                incoming.snapshotVersion <= local.snapshotVersion
        return !incomingWouldReopenOlder
    }

    // ---------- Category assignments ----------

    fun assignmentToCloud(assignment: CategoryAssignment): Map<String, Any?> = mapOf(
        "sessionId" to assignment.sessionId,
        "category" to assignment.category,
        "assigneeIds" to assignment.assigneeIds.toList(),
        "status" to assignment.status.name,
        "updatedAtLocal" to assignment.updatedAt
    )

    /** Null when session or category is missing. */
    fun assignmentFromCloud(map: Map<String, Any?>, nowLocal: Long): CategoryAssignment? {
        val sessionId = map["sessionId"]?.toString().orEmpty()
        val category = map["category"]?.toString().orEmpty()
        if (sessionId.isBlank() || category.isBlank()) return null
        return CategoryAssignment(
            sessionId = sessionId,
            category = category,
            assigneeIds = (map["assigneeIds"] as? List<*>)?.mapNotNull { it?.toString() }?.toSet().orEmpty(),
            status = runCatching { CategoryWorkStatus.valueOf(map["status"]?.toString().orEmpty()) }
                .getOrDefault(CategoryWorkStatus.NOT_STARTED),
            updatedAt = (map["updatedAtLocal"] as? Number)?.toLong() ?: nowLocal
        )
    }

    // ---------- Count entries ----------

    /**
     * Merges a server count entry into the local one. Returns null (keep local) while this device
     * has its own different edit waiting to upload, so a snapshot never overwrites unsent work.
     */
    fun mergeRemoteCount(local: InventoryCountEntry?, incoming: RemoteCountEntry): InventoryCountEntry? {
        val hasDifferentPendingWrite = local?.pendingCloud == true && local.lastEventId != incoming.lastEventId
        if (hasDifferentPendingWrite) return null
        return InventoryCountEntry(
            sessionId = incoming.sessionId,
            productId = incoming.productId,
            departmentId = incoming.departmentId,
            quantity = incoming.quantity,
            note = incoming.note,
            updatedBy = incoming.updatedBy,
            updatedAt = incoming.updatedAt,
            version = incoming.version,
            lastEventId = incoming.lastEventId,
            pendingCloud = false
        )
    }

    /**
     * Applies a server acknowledgement of [write] at [newVersion] to the local entry. If a newer
     * local edit is still pending, only the confirmed version is recorded so that edit's upload is
     * based on it. Returns the entry unchanged when there is nothing to update.
     */
    fun applyAcknowledgement(local: InventoryCountEntry, write: PendingCountWrite, newVersion: Int, nowLocal: Long): InventoryCountEntry =
        when {
            local.lastEventId == write.eventId -> local.copy(version = newVersion, pendingCloud = false, updatedAt = nowLocal)
            newVersion > local.version -> local.copy(version = newVersion)
            else -> local
        }

    // ---------- Historikk (closed-month snapshots) ----------

    /** Document id of a snapshot: one per month (closing again replaces it). */
    fun historyDocumentId(snapshot: CountSession): String = snapshot.inventorySessionId?.takeIf { it.isNotBlank() } ?: snapshot.id

    /** companies/{c}/locations/{l}/history/{id}: the closed month with every counted line. */
    fun historyToCloud(snapshot: CountSession): Map<String, Any?> = mapOf(
        "id" to snapshot.id,
        "inventorySessionId" to snapshot.inventorySessionId,
        "companyId" to snapshot.companyId,
        "locationId" to snapshot.locationId,
        "locationName" to snapshot.locationName,
        "yearMonth" to snapshot.yearMonth,
        "userId" to snapshot.userId,
        "userName" to snapshot.userName,
        "createdAtLocal" to snapshot.createdAt,
        "lines" to snapshot.lines.map { line ->
            mapOf(
                "productId" to line.productId, "productName" to line.productName, "unit" to line.unit,
                "pricePerUnit" to line.pricePerUnit, "vat" to line.vat, "quantity" to line.quantity,
                "departmentName" to line.departmentName, "note" to line.note
            )
        }
    )

    fun historyFromCloud(map: Map<String, Any?>): CountSession? {
        val id = map["id"]?.toString().orEmpty()
        val yearMonth = map["yearMonth"]?.toString().orEmpty()
        if (id.isBlank() || yearMonth.isBlank()) return null
        val lines = (map["lines"] as? List<*>).orEmpty().mapNotNull { raw ->
            val line = raw as? Map<*, *> ?: return@mapNotNull null
            CountLineSnapshot(
                productId = line["productId"]?.toString().orEmpty(),
                productName = line["productName"]?.toString().orEmpty(),
                unit = line["unit"]?.toString().orEmpty(),
                pricePerUnit = (line["pricePerUnit"] as? Number)?.toDouble() ?: 0.0,
                vat = (line["vat"] as? Number)?.toDouble() ?: 0.0,
                quantity = (line["quantity"] as? Number)?.toDouble() ?: 0.0,
                departmentName = line["departmentName"]?.toString().orEmpty(),
                note = line["note"]?.toString().orEmpty()
            )
        }
        return CountSession(
            id = id,
            inventorySessionId = map["inventorySessionId"]?.toString()?.takeIf { it.isNotBlank() },
            companyId = map["companyId"]?.toString().orEmpty(),
            locationId = map["locationId"]?.toString().orEmpty(),
            locationName = map["locationName"]?.toString().orEmpty(),
            yearMonth = yearMonth,
            userId = map["userId"]?.toString().orEmpty(),
            userName = map["userName"]?.toString().orEmpty(),
            createdAt = (map["createdAtLocal"] as? Number)?.toLong() ?: 0L,
            lines = lines,
            syncState = CloudSaveState.CLOUD_CONFIRMED
        )
    }

    // ---------- Hovedvarer (catalog: every hovedvare, its place and the order of its products) ----------

    /**
     * Document id of a hovedvare: readable part plus a hash of the name (ignoring case), so names
     * that only differ in punctuation ("Frukt/Grønt", "Frukt Grønt") never share a document.
     */
    fun categoryDocumentId(name: String): String {
        var hash = 0x811c9dc5.toInt()
        name.trim().lowercase().forEach { ch -> hash = (hash xor ch.code) * 0x01000193 }
        val readable = safeDocumentId(name).take(40).trim('-').ifBlank { "hovedvare" }
        return "$readable-" + (hash.toLong() and 0xffffffffL).toString(36)
    }

    fun categoryToCloud(doc: CategoryDoc, nowLocal: Long): Map<String, Any?> = mapOf(
        "name" to doc.name,
        "sortIndex" to doc.sortIndex,
        "productOrder" to doc.productOrder,
        "updatedAtLocal" to nowLocal
    )

    fun categoryFromCloud(map: Map<String, Any?>): CategoryDoc? {
        val name = map["name"]?.toString()?.trim().orEmpty()
        if (name.isBlank()) return null
        return CategoryDoc(
            name = name,
            sortIndex = (map["sortIndex"] as? Number)?.toInt(),
            productOrder = (map["productOrder"] as? List<*>)?.mapNotNull { it?.toString() }.orEmpty()
        )
    }

    /** The catalog documents that describe [categories] in [order] with saved product orders. */
    fun catalogDocs(categories: List<String>, order: List<String>, productOrders: Map<String, List<String>>): Map<String, CategoryDoc> =
        categories.associate { name ->
            val index = order.indexOf(name)
            categoryDocumentId(name) to CategoryDoc(name, index.takeIf { it >= 0 }, productOrders[name].orEmpty())
        }

    // ---------- Logg (audit) and stikkprøver ----------

    /**
     * Count edits are not sent to the audit log in the cloud: every count already carries who and
     * when in its own document, and doubling those writes would eat the free Firebase quota.
     */
    fun auditGoesToCloud(event: AuditEvent): Boolean = event.entityType != "CountEntry"

    fun auditToCloud(event: AuditEvent): Map<String, Any?> = mapOf(
        "id" to event.id, "timestamp" to event.timestamp, "actorUserId" to event.actorUserId, "actorName" to event.actorName,
        "action" to event.action, "entityType" to event.entityType, "entityId" to event.entityId, "field" to event.field,
        "beforeValue" to event.beforeValue, "afterValue" to event.afterValue, "source" to event.source,
        "deviceId" to event.deviceId, "appVersion" to event.appVersion, "companyId" to event.companyId,
        "inventorySessionId" to event.inventorySessionId, "category" to event.category, "productId" to event.productId
    )

    fun auditFromCloud(map: Map<String, Any?>): AuditEvent? {
        val id = map["id"]?.toString().orEmpty()
        // changeEvents also holds the count events (eventId/entity, no action): not log entries.
        if (id.isBlank() || map["action"] == null) return null
        fun str(key: String) = map[key]?.toString().orEmpty()
        return AuditEvent(
            id = id, timestamp = (map["timestamp"] as? Number)?.toLong() ?: 0L,
            actorUserId = str("actorUserId"), actorName = str("actorName"), action = str("action"),
            entityType = str("entityType"), entityId = str("entityId"), field = str("field"),
            beforeValue = str("beforeValue"), afterValue = str("afterValue"), source = str("source").ifBlank { "app" },
            deviceId = str("deviceId"), appVersion = str("appVersion"), companyId = str("companyId"),
            inventorySessionId = map["inventorySessionId"]?.toString()?.takeIf { it.isNotBlank() },
            category = map["category"]?.toString()?.takeIf { it.isNotBlank() },
            productId = map["productId"]?.toString()?.takeIf { it.isNotBlank() }
        )
    }

    fun spotCheckToCloud(record: SpotCheckRecord): Map<String, Any?> = mapOf(
        "id" to record.id, "sessionId" to record.sessionId, "productId" to record.productId, "productName" to record.productName,
        "expectedQty" to record.expectedQty, "checkedQty" to record.checkedQty, "checkerUserId" to record.checkerUserId,
        "checkerName" to record.checkerName, "createdAtLocal" to record.createdAt, "matches" to record.matches
    )

    fun spotCheckFromCloud(map: Map<String, Any?>): SpotCheckRecord? {
        val id = map["id"]?.toString().orEmpty()
        if (id.isBlank()) return null
        return SpotCheckRecord(
            id = id, sessionId = map["sessionId"]?.toString()?.takeIf { it.isNotBlank() },
            productId = map["productId"]?.toString().orEmpty(), productName = map["productName"]?.toString().orEmpty(),
            expectedQty = (map["expectedQty"] as? Number)?.toDouble() ?: 0.0, checkedQty = (map["checkedQty"] as? Number)?.toDouble() ?: 0.0,
            checkerUserId = map["checkerUserId"]?.toString().orEmpty(), checkerName = map["checkerName"]?.toString().orEmpty(),
            createdAt = (map["createdAtLocal"] as? Number)?.toLong() ?: 0L, matches = map["matches"] as? Boolean ?: false
        )
    }
}

/** Svinn: companies/{c}/locations/{l}/svinn/{id}. */
object SvinnMapping {
    fun toCloud(r: SvinnRecord): Map<String, Any?> = mapOf(
        "id" to r.id, "yearMonth" to r.yearMonth, "sessionId" to r.sessionId, "productId" to r.productId,
        "productName" to r.productName, "category" to r.category, "unit" to r.unit, "pricePerUnit" to r.pricePerUnit,
        "quantity" to r.quantity, "reason" to r.reason.name, "note" to r.note, "userId" to r.userId,
        "userName" to r.userName, "createdAtLocal" to r.createdAt, "updatedAtLocal" to r.updatedAt
    )

    fun fromCloud(map: Map<String, Any?>): SvinnRecord? {
        val id = map["id"]?.toString().orEmpty()
        val productId = map["productId"]?.toString().orEmpty()
        val yearMonth = map["yearMonth"]?.toString().orEmpty()
        if (id.isBlank() || productId.isBlank() || yearMonth.isBlank()) return null
        fun str(key: String) = map[key]?.toString().orEmpty()
        val created = (map["createdAtLocal"] as? Number)?.toLong() ?: 0L
        return SvinnRecord(
            id = id, yearMonth = yearMonth, sessionId = map["sessionId"]?.toString()?.takeIf { it.isNotBlank() },
            productId = productId, productName = str("productName"), category = str("category"), unit = str("unit"),
            pricePerUnit = (map["pricePerUnit"] as? Number)?.toDouble() ?: 0.0, quantity = (map["quantity"] as? Number)?.toDouble() ?: 0.0,
            reason = SvinnReason.parse(map["reason"]?.toString()), note = str("note"), userId = str("userId"), userName = str("userName"),
            createdAt = created, updatedAt = (map["updatedAtLocal"] as? Number)?.toLong() ?: created
        )
    }
}

/** One hovedvare in the cloud catalog: its name, place in the order (null = alphabetical) and saved product order. */
data class CategoryDoc(val name: String, val sortIndex: Int?, val productOrder: List<String>)
